/**
 * What a signed-in user's role may reach, as decided by the API (RolePermission.scope) and sent at login as
 * `roleScope`. UI code asks these helpers instead of comparing role names, so adding or renaming a role never
 * means hunting for string lists — the API's role definition is the only place that changes.
 *
 *  ALL_CHAPTERS  system administrator and the NEC tier — every chapter
 *  OWN_CHAPTER   chapter president / executive committee — the chapter they belong to
 *  SELF          members and non-members — their own records only
 *
 * The API enforces all of this itself; the UI only decides what to show.
 */
export type RoleScope = 'ALL_CHAPTERS' | 'OWN_CHAPTER' | 'SELF';

const SCOPES: RoleScope[] = ['ALL_CHAPTERS', 'OWN_CHAPTER', 'SELF'];

/** The scope stored at login. A session that predates it (or has none) gets the most restricted view until it signs in again. */
export function storedRoleScope(): RoleScope {
    try {
        const raw = sessionStorage.getItem('roleScope') as RoleScope | null;
        return raw && SCOPES.includes(raw) ? raw : 'SELF';
    } catch {
        return 'SELF';
    }
}

export function reachesAllChapters(scope: RoleScope): boolean {
    return scope === 'ALL_CHAPTERS';
}

/** True when the role's reach covers {@code chapterId}: every chapter, or the user's own chapter. */
export function reachesChapter(scope: RoleScope, userChapter: unknown, chapterId: unknown): boolean {
    if (scope === 'ALL_CHAPTERS') return true;
    return scope === 'OWN_CHAPTER' && userChapter != null && String(userChapter) === String(chapterId);
}

/** Permission check against the `rolesPermission` map the API sends at login, e.g. can(perms, 'EVENT', 'CREATE'). */
export function can(permissions: any, resource: string, action: string): boolean {
    return !!permissions?.[resource]?.includes(action);
}
